import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  TextInput,
  Skeleton,
  Text,
  UnstyledButton,
  Group,
  Stack,
  Center,
  Loader,
} from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import { InlineIcon } from '@iconify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useIntersection } from '@mantine/hooks';
import dayjs from 'dayjs';
import { UserAvatar } from 'components/UserAvatar/UserAvatar';
import { searchAiCoachAssistantUsers } from 'api/users';
import type { AiCoachAssistantUser } from 'api/users';
import { getUserDisplayName } from 'views/Users/helpers/UsersViewUtils';
import { useMe } from 'store/userStore';
import { useAiCoachAssistantStore } from 'store/aiCoachAssistantStore';

const ITEMS_PER_PAGE = 30;

const useStyles = createStyles((theme, _, u) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  searchBox: {
    padding: theme.spacing.sm,
    borderBottom: theme.other.elements.appBorder,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: `${theme.spacing.xs} 0`,
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    width: '100%',
    cursor: 'pointer',
    transition: 'background-color 100ms ease',
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
      [u.dark]: { backgroundColor: theme.colors.dark[5] },
    },
  },
  userItemSelected: {
    backgroundColor: theme.colors.teal[0],
    [u.dark]: { backgroundColor: theme.colors.dark[4] },
    '&:hover': {
      backgroundColor: theme.colors.teal[0],
      [u.dark]: { backgroundColor: theme.colors.dark[4] },
    },
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  empty: {
    padding: theme.spacing.xl,
    textAlign: 'center',
    color: theme.colors.gray[5],
  },
}));

const getStatusBadge = (user: AiCoachAssistantUser) => {
  const bucket = user.followupBucket;
  if (!bucket) return null;

  if (bucket.isDisengaged)
    return (
      <Badge size="xs" color="red" variant="filled">
        Disengaged
      </Badge>
    );
  if (bucket.isFollowupRequired)
    return (
      <Badge size="xs" color="orange" variant="filled">
        Follow-up
      </Badge>
    );
  if (bucket.isMeetingCoachToday)
    return (
      <Badge size="xs" color="green" variant="filled">
        Meeting today
      </Badge>
    );
  if (bucket.isAwaitingResponse)
    return (
      <Badge size="xs" color="blue" variant="light">
        Awaiting
      </Badge>
    );

  return null;
};

export const UserList = () => {
  const { classes, cx } = useStyles();
  const me = useMe();
  const selectedUserId = useAiCoachAssistantStore((s) => s.selectedUserId);
  const selectUser = useAiCoachAssistantStore((s) => s.selectUser);
  const [search, setSearch] = useState('');

  const { ref: loadMoreRef, entry } = useIntersection({ threshold: 1 });

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useInfiniteQuery(
      ['ai-coach-assistant-users', me?.userId, search],
      ({ pageParam = 1 }) =>
        searchAiCoachAssistantUsers({
          textSearch: search || undefined,
          limit: ITEMS_PER_PAGE,
          page: pageParam,
        }),
      {
        getNextPageParam: (lastPage, allPages) => {
          if (!lastPage?.items) return undefined;
          const nextPage = allPages.length + 1;
          return lastPage.items.length === ITEMS_PER_PAGE
            ? nextPage
            : undefined;
        },
        enabled: !!me?.userId,
        keepPreviousData: true,
        staleTime: 1000 * 30,
      }
    );

  useEffect(() => {
    if (entry?.isIntersecting && !isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [entry?.isIntersecting, fetchNextPage, isFetching, hasNextPage]);

  const users = data?.pages.flatMap((page) => page?.items ?? []) ?? [];

  return (
    <Box className={classes.container}>
      <Box className={classes.searchBox}>
        <TextInput
          placeholder="Search users..."
          size="sm"
          leftSection={<InlineIcon icon="tabler:search" height={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Box>

      <Box className={classes.list}>
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ padding: '8px 12px' }}>
              <Skeleton height={44} radius="sm" />
            </Box>
          ))}

        {!isLoading && users.length === 0 && (
          <Text className={classes.empty}>No users found</Text>
        )}

        {users.map((user) => (
          <UnstyledButton
            key={user.userId}
            className={cx(classes.userItem, {
              [classes.userItemSelected]: selectedUserId === user.userId,
            })}
            onClick={() => selectUser(user.userId)}>
            <UserAvatar user={user} size={36} />
            <Stack gap={2} className={classes.userInfo}>
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" fw={500} truncate>
                  {getUserDisplayName(user)}
                </Text>
                {getStatusBadge(user)}
              </Group>
              {user.lastAssistantInteractionAt ? (
                <Text size="xs" c="dimmed" truncate>
                  Last AI chat{' '}
                  {dayjs(user.lastAssistantInteractionAt).fromNow()}
                </Text>
              ) : (
                user.lastSeenDate && (
                  <Text size="xs" c="dimmed" truncate>
                    Last seen {dayjs(user.lastSeenDate).fromNow()}
                  </Text>
                )
              )}
            </Stack>
          </UnstyledButton>
        ))}

        <div ref={loadMoreRef}>
          {isFetching && !isLoading && (
            <Center py="sm">
              <Loader size="sm" />
            </Center>
          )}
        </div>
      </Box>
    </Box>
  );
};
